import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DeviceRow from './DeviceRow';
import { Device } from "../types";
import { OtaMode } from '../utils/otaMode';
import { API_BASE_URL } from '../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faAngleLeft, 
  faAngleRight, 
  faAnglesLeft, 
  faAnglesRight,
  faSortUp,
  faSortDown,
  faCircleInfo
} from '@fortawesome/free-solid-svg-icons';

const DEFAULT_API_BASE_URL = API_BASE_URL;

interface DeviceConnectivity {
  mqttOnline: boolean | null;
  mqttLastSeenAt: string | null;
  mqttDisconnectReason: string | null;
}

interface DeviceTableProps {
  devices: Device[];
  loading: boolean;
  error: string | null;
  apiBaseUrl?: string;
  onToggleMarked: (serial: string, value: OtaMode) => void;
  onUpdateLoggingMode: (serial: string, mode: number) => void;
  onFirmwareInstall?: (device: Device) => void;
  onAlertsOpen?: (device: Device) => void;
  updatingOTA?: string | null; // Add this to track which device is being updated
  
  // Sorting props
  sortColumn?: string;
  sortDirection?: string;
  onSortChange?: (column: string) => void;
  
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalDevices?: number;
  onPageChange?: (page: number) => void;
  showPagination?: boolean;
  showManagementControls?: boolean;
  showOwnerColumns?: boolean;
}

const DeviceTable: React.FC<DeviceTableProps> = ({ 
  devices, 
  loading, 
  error,
  apiBaseUrl = DEFAULT_API_BASE_URL,
  onToggleMarked,
  onUpdateLoggingMode,
  onFirmwareInstall,
  onAlertsOpen,
  updatingOTA = null,
  sortColumn = 'model',
  sortDirection = 'asc',
  onSortChange,
  currentPage = 1,
  totalPages = 1,
  totalDevices = 0,
  onPageChange,
  showPagination = false,
  showManagementControls = true,
  showOwnerColumns = true,
}) => {
  const [showLoggingInfoModal, setShowLoggingInfoModal] = useState(false);

  // Online/offline status is fetched here (not with the device list) so the
  // table can render immediately; each status dot shows a spinner until its
  // serial appears in this map.
  const [connectivity, setConnectivity] = useState<Record<string, DeviceConnectivity>>({});

  const serialsKey = devices.map((device) => device.serial).join(',');

  useEffect(() => {
    const serials = devices.map((device) => device.serial).filter(Boolean);

    // Reset to "pending" whenever the set of devices changes (e.g. page change).
    setConnectivity({});

    if (serials.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await axios.post(
          `${apiBaseUrl}/devices/connectivity`,
          { serials },
          { withCredentials: true }
        );
        if (!cancelled) {
          setConnectivity(response.data.connectivity || {});
        }
      } catch (error) {
        // On failure, resolve every serial as "unavailable" so the indicators
        // stop spinning instead of hanging forever.
        if (!cancelled) {
          const fallback: Record<string, DeviceConnectivity> = {};
          serials.forEach((serial) => {
            fallback[serial] = {
              mqttOnline: null,
              mqttLastSeenAt: null,
              mqttDisconnectReason: 'PingUnavailable',
            };
          });
          setConnectivity(fallback);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialsKey, apiBaseUrl]);

  // Initialize tooltips when component mounts
  useEffect(() => {
    // Initialize all tooltips
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => {
      return new (window as any).bootstrap.Tooltip(tooltipTriggerEl, {
        html: true,
        placement: 'top'
      });
    });

    // Cleanup tooltips when component unmounts
    return () => {
      tooltipList.forEach(tooltip => tooltip.dispose());
    };
  }, []);
  
  // Function to sort columns (if sorting functionality is enabled)
  const handleSort = (column: string) => {
    if (onSortChange) {
      onSortChange(column);
    }
  };
  
  // Function to render sort indicators with appropriate styling
  const renderSortIndicator = (column: string) => {
    // Show up arrow
    const upColor = (column === sortColumn && sortDirection === 'asc') ? '#333333' : '#dadee3';
    
    // Show down arrow
    const downColor = (column === sortColumn && sortDirection === 'desc') ? '#333333' : '#dadee3';
    
    return (
      <span className="ms-1" style={{ display: 'inline-block', position: 'relative', width: '10px', height: '14px' }}>
        <FontAwesomeIcon 
          icon={faSortUp} 
          style={{ 
            color: upColor,
            position: 'absolute',
            top: '-1px',
            left: 0
          }} 
        />
        <FontAwesomeIcon 
          icon={faSortDown} 
          style={{ 
            color: downColor,
            position: 'absolute',
            bottom: '-2px',
            left: 0
          }} 
        />
      </span>
    );
  };
  
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '280px' }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }
  
  return (
    <>
      <table className="table table-striped">
        <thead>
          <tr>
            <th style={{ width: '28px', minWidth: '28px', paddingLeft: '0.25rem', paddingRight: '0.25rem' }}></th>
            <th>Serial</th>
            <th 
              onClick={() => onSortChange && handleSort('model')} 
              style={{cursor: onSortChange ? 'pointer' : 'default'}}
            >
              Model {onSortChange && renderSortIndicator('model')}
            </th>
            <th 
              onClick={() => onSortChange && handleSort('version')} 
              style={{cursor: onSortChange ? 'pointer' : 'default'}}
            >
              Version {onSortChange && renderSortIndicator('version')}
            </th>
            <th 
              onClick={() => onSortChange && handleSort('hardware_revision')} 
              style={{cursor: onSortChange ? 'pointer' : 'default'}}
            >
              HW Rev {onSortChange && renderSortIndicator('hardware_revision')}
            </th>
            <th 
              onClick={() => onSortChange && handleSort('type')} 
              style={{cursor: onSortChange ? 'pointer' : 'default'}}
            >
              Deployment {onSortChange && renderSortIndicator('type')}
            </th>
            {showOwnerColumns && (
              <>
                <th>Group</th>
                <th>User</th>
              </>
            )}
            <th>Last Check-In</th>
            {showManagementControls && (
              <>
                <th>
                  Logging{' '}
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-1 align-middle"
                    onClick={() => setShowLoggingInfoModal(true)}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="text-secondary"
                      size="sm"
                      aria-label="More information about logging modes"
                    />
                  </button>
                </th>
                <th>OTA</th>
                <th>Actions</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {devices.length > 0 ? (
            devices.map((device) => {
              const deviceConnectivity = connectivity[device.serial];
              const rowDevice = deviceConnectivity
                ? { ...device, ...deviceConnectivity }
                : device;
              return (
                <DeviceRow
                  key={device._id}
                  device={rowDevice}
                  connectivityPending={!deviceConnectivity}
                  userName={device.user
                    ? `${device.user.firstName || ''} ${device.user.lastName || ''}`.trim()
                    : '-'
                  }
                  onToggleMarked={onToggleMarked}
                  onUpdateLoggingMode={onUpdateLoggingMode}
                  onFirmwareInstall={onFirmwareInstall}
                  onAlertsOpen={onAlertsOpen}
                  isUpdatingOTA={updatingOTA === device.serial}
                  showManagementControls={showManagementControls}
                  showOwnerColumns={showOwnerColumns}
                />
              );
            })
          ) : (
            <tr>
              <td
                colSpan={
                  7 + (showOwnerColumns ? 2 : 0) + (showManagementControls ? 3 : 0)
                }
                className="text-center"
              >
                No devices found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      {showPagination && totalPages > 0 && (
        <div className="responsive-pagination d-flex justify-content-between align-items-center mt-2">
          <div className="fw-bold pt-1">
            {totalDevices.toLocaleString()} total devices
          </div>
          <nav aria-label="Device pagination">
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => onPageChange && onPageChange(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                >
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => onPageChange && onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
              </li>
              
              <li className="page-item disabled">
                <span className="page-link">
                  Page {currentPage} of {totalPages}
                </span>
              </li>
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange && onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange && onPageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                >
                  <FontAwesomeIcon icon={faAnglesRight} />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Logging Info Modal */}
      {showLoggingInfoModal && (
        <div className="modal-wrapper" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}>
          <div 
            className="modal-backdrop show" 
            onClick={() => setShowLoggingInfoModal(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
          ></div>
          <div 
            className="modal d-block" 
            tabIndex={-1} 
            role="dialog"
            aria-labelledby="loggingInfoModalLabel" 
            aria-hidden="true"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="loggingInfoModalLabel">Logging Modes</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    aria-label="Close"
                    onClick={() => setShowLoggingInfoModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <h6>Available Logging Levels:</h6>
                  <ul className="list-unstyled">
                    <li className="mb-2"><strong>0 - No Logs:</strong> Disables all logging</li>
                    <li className="mb-2"><strong>1 - Errors:</strong> Only crashes and errors</li>
                    <li className="mb-2"><strong>2 - Warnings:</strong> Crashes, errors, and warnings</li>
                    <li className="mb-2"><strong>3 - Info:</strong> Crashes, errors, warnings, and info</li>
                    <li className="mb-2"><strong>4 - Debug:</strong> All logs including debug</li>
                  </ul>
                  <div className="alert alert-info">
                    <small>
                      Higher logging levels will use more network bandwidth and storage space and may impact device performance and increase costs on the cloud.
                      Choose the appropriate level based on your debugging needs.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowLoggingInfoModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeviceTable;